import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Cpu, HardDrive, Network, RefreshCw, Server } from 'lucide-react';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const formatUptime = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${h}h ${m}m`;
};

const MetricCard = ({ title, value, subtitle, icon: Icon }) => (
  <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs text-secondary-500">{title}</p>
        <p className="text-xl font-semibold text-secondary-900 dark:text-white truncate">{value}</p>
        {subtitle && <p className="text-xs text-secondary-500 mt-0.5">{subtitle}</p>}
      </div>
      {Icon && (
        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600">
          <Icon className="h-4 w-4" />
        </div>
      )}
    </div>
  </div>
);

const ProcessTable = ({ title, rows, valueKey, valueLabel }) => (
  <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-secondary-100 dark:border-secondary-700">
      <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-xs uppercase text-secondary-500 bg-secondary-50 dark:bg-secondary-900/40">
          <tr>
            <th className="px-3 py-2 text-left">PID</th>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">User</th>
            <th className="px-3 py-2 text-right">{valueLabel}</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
          {(rows || []).map((row) => (
            <tr key={`${row.pid}-${row.name}`}>
              <td className="px-3 py-2 font-mono text-xs">{row.pid}</td>
              <td className="px-3 py-2 truncate max-w-[180px]" title={row.name}>
                {row.name}
              </td>
              <td className="px-3 py-2 text-xs text-secondary-500">{row.username || '—'}</td>
              <td className="px-3 py-2 text-right font-medium">
                {typeof row[valueKey] === 'number' ? row[valueKey].toFixed(1) : row[valueKey] ?? '—'}
                {valueKey.includes('percent') ? '%' : ''}
              </td>
              <td className="px-3 py-2 text-xs capitalize text-secondary-500">{row.status || '—'}</td>
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr>
              <td colSpan={5} className="px-3 py-8 text-center text-secondary-500">
                No process data
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const SystemDiagnosticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/monitoring/system-logs', {
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchData, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchData]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-500">
          Live machine metrics from the API host{data?.timestamp ? ` · ${new Date(data.timestamp).toLocaleString()}` : ''}
        </p>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-secondary-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-secondary-300"
            />
            Auto-refresh (15s)
          </label>
          <button type="button" onClick={fetchData} className="btn-secondary inline-flex items-center gap-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard
          title="CPU"
          value={`${data?.cpu?.percent ?? '—'}%`}
          subtitle={`${data?.cpu?.count_logical ?? data?.cpu?.count ?? '—'} cores`}
          icon={Cpu}
        />
        <MetricCard
          title="Memory"
          value={`${data?.memory?.percent ?? '—'}%`}
          subtitle={`${data?.memory?.available_gb ?? '—'} GB available of ${data?.memory?.total_gb ?? '—'} GB`}
          icon={HardDrive}
        />
        <MetricCard
          title="Disk"
          value={`${data?.disk?.percent ?? '—'}%`}
          subtitle={`${data?.disk?.free_gb ?? '—'} GB free`}
          icon={HardDrive}
        />
        <MetricCard
          title="Host uptime"
          value={formatUptime(data?.uptime_seconds)}
          subtitle={data?.platform?.system || '—'}
          icon={Server}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Cpu className="h-4 w-4" /> Load average
          </h3>
          {data?.load_average ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                ['1m', data.load_average['1m']],
                ['5m', data.load_average['5m']],
                ['15m', data.load_average['15m']],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-xs text-secondary-500">{label}</p>
                  <p className="text-lg font-semibold">{typeof val === 'number' ? val.toFixed(2) : '—'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-secondary-500">Not available on this platform</p>
          )}
        </div>

        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <HardDrive className="h-4 w-4" /> Disk I/O
          </h3>
          {data?.disk?.io ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-secondary-500">Read</span><span>{data.disk.io.read_mb} MB</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">Write</span><span>{data.disk.io.write_mb} MB</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">Read ops</span><span>{data.disk.io.read_count}</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">Write ops</span><span>{data.disk.io.write_count}</span></div>
            </div>
          ) : (
            <p className="text-sm text-secondary-500">I/O counters unavailable</p>
          )}
        </div>

        <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Network className="h-4 w-4" /> Network
          </h3>
          {data?.network ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-secondary-500">Connections</span><span>{data.network.connections}</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">Sent</span><span>{data.network.io?.bytes_sent_mb ?? '—'} MB</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">Received</span><span>{data.network.io?.bytes_recv_mb ?? '—'} MB</span></div>
              <div className="flex justify-between"><span className="text-secondary-500">API process</span><span>{data.process?.memory_mb ?? '—'} MB · {data.process?.threads ?? '—'} threads</span></div>
            </div>
          ) : (
            <p className="text-sm text-secondary-500">Network data unavailable</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ProcessTable title="Top CPU processes" rows={data?.top_cpu} valueKey="cpu_percent" valueLabel="CPU %" />
        <ProcessTable title="Top memory processes" rows={data?.top_memory} valueKey="memory_percent" valueLabel="Mem %" />
      </div>
    </div>
  );
};

export default SystemDiagnosticsTab;
